/* eslint @typescript-eslint/strict-boolean-expressions: off */

import styles from "./SelectResourceOwner.module.css";
import React, { useState } from "react";
import Link from "next/link";
import { Avatar } from "../../avatar/avatar";
import { Icon } from "../../icon/icon";
import cn from "classnames";
import { Owner } from "lib/entries/types";
import { QueryOwnershipChangeWithMatView } from "gui/editor/MatView/QueryOwnershipChangeWithMatView";
import { ControlledClickPopover } from "components/ClickPopover";

interface Props {
  eligibleOwners?: Array<Owner>;
  onOwnerChange?: (owner: Owner) => void;
  owner: Owner;
  title: string;
  disabled?: boolean;
  hasMatView?: boolean;
}

interface EligibleOwnerGroups {
  teams: Array<Owner>;
  user: Owner | undefined;
}

export const SelectResourceOwner = (props: Props) => {
  const [isOpened, setIsOpened] = useState(false);

  const {
    disabled,
    owner: activeOwner,
    onOwnerChange,
    eligibleOwners = [],
  } = props;

  const eligibleOwnerGroups = eligibleOwners.reduce<EligibleOwnerGroups>(
    (acc, owner) => {
      switch (owner.type) {
        case "user":
          acc.user = owner;
          return acc;
        case "team":
          acc.teams.push(owner);
          return acc;
      }
    },
    { teams: [], user: undefined }
  );

  if (
    disabled ||
    !onOwnerChange ||
    !eligibleOwnerGroups.user ||
    eligibleOwners.length <= 1
  ) {
    return (
      <Link href={`/${activeOwner.handle}`}>
        <a>@{activeOwner.handle}</a>
      </Link>
    );
  }

  const createOnClickHandler = (owner: Owner) => () => {
    setIsOpened(false);
    onOwnerChange(owner);
  };

  const isCurrentOwner = (owner: Owner) => {
    return owner.id === activeOwner.id && owner.type === activeOwner.type;
  };

  return (
    <ControlledClickPopover
      isOpen={isOpened}
      setIsOpen={setIsOpened}
      position="below-align-left"
      content={() =>
        eligibleOwnerGroups.user && (
          <div
            className={styles.body}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <h3>Your account</h3>
            <DropdownItem
              selected={isCurrentOwner(eligibleOwnerGroups.user)}
              owner={eligibleOwnerGroups.user}
              onClick={createOnClickHandler(eligibleOwnerGroups.user)}
              hasMatView={props.hasMatView}
              title={props.title}
            />
            {eligibleOwnerGroups.teams.length > 0 && (
              <>
                <hr />
                <h3>Teams</h3>
                <div className={styles.buttonGroup}>
                  {eligibleOwnerGroups.teams.map((owner) => (
                    <DropdownItem
                      key={owner.handle}
                      owner={owner}
                      selected={isCurrentOwner(owner)}
                      onClick={createOnClickHandler(owner)}
                      hasMatView={props.hasMatView}
                      title={props.title}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )
      }
    >
      <button className={cn(styles.trigger, isOpened && styles.active)}>
        <span>@{activeOwner.handle}</span>
        <Icon icon={isOpened ? "caret-up-fill" : "caret-down-fill"} />
      </button>
    </ControlledClickPopover>
  );
};

const DropdownItem = ({
  selected,
  owner,
  onClick,
  hasMatView,
  title,
}: {
  selected: boolean;
  owner: Owner;
  onClick: () => void;
  hasMatView?: boolean;
  title: string;
}) => {
  function SelectButton({ onClick }: { onClick?: () => void }) {
    return (
      <button className={cn(selected && styles.active)} onClick={onClick}>
        <Avatar src={owner.profile_image_url} size={16} />
        <span>@{owner.handle}</span>
        {selected && <Icon icon="simple-check" />}
      </button>
    );
  }

  return hasMatView ? (
    <QueryOwnershipChangeWithMatView
      matViewName={title}
      newOwner={owner}
      onChangeOwner={onClick}
    >
      <SelectButton />
    </QueryOwnershipChangeWithMatView>
  ) : (
    <SelectButton onClick={onClick} />
  );
};
